import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Slide,
  Stack,
  Typography,
} from "@mui/material";
import { IoClose } from "react-icons/io5";
import { HiOutlinePencil, HiOutlineTrash } from "react-icons/hi2";

const BLUE = "#0C86B6";
const BLUE_DARK = "#0a6b92";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Handwritten signature captured on a canvas and handed back as a PNG File so
// it can ride along with the other documents on the multipart upload.
const SignatureDialog = ({ open, onClose, onSave }) => {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const [saving, setSaving] = useState(false);

  // The canvas is sized from its rendered box (and the device pixel ratio) so
  // the stroke lands exactly under the pointer on retina screens too.
  const prepareCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const { width, height } = canvas.getBoundingClientRect();
    if (!width || !height) return;
    canvas.width = width * ratio;
    canvas.height = height * ratio;
    const ctx = canvas.getContext("2d");
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#111827";
  }, []);

  useEffect(() => {
    if (!open) return;
    setIsEmpty(true);
    // Wait for the dialog transition to settle before measuring the canvas.
    // Deliberately not re-sizing on window resize: re-preparing the canvas
    // clears it, and losing a half-drawn signature is worse than a stretched box.
    const id = window.setTimeout(prepareCanvas, 250);
    return () => window.clearTimeout(id);
  }, [open, prepareCanvas]);

  const pointFromEvent = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const handlePointerDown = (e) => {
    e.preventDefault();
    canvasRef.current.setPointerCapture?.(e.pointerId);
    drawingRef.current = true;
    lastPointRef.current = pointFromEvent(e);
  };

  const handlePointerMove = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const point = pointFromEvent(e);
    const from = lastPointRef.current;
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
    lastPointRef.current = point;
    if (isEmpty) setIsEmpty(false);
  };

  const handlePointerUp = (e) => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    canvasRef.current?.releasePointerCapture?.(e.pointerId);
    // A single tap leaves no line segment, so draw a dot for it.
    if (isEmpty) {
      const ctx = canvasRef.current.getContext("2d");
      const point = lastPointRef.current;
      ctx.beginPath();
      ctx.arc(point.x, point.y, 1.5, 0, Math.PI * 2);
      ctx.fillStyle = "#111827";
      ctx.fill();
      setIsEmpty(false);
    }
  };

  const handleClear = () => {
    prepareCanvas();
    setIsEmpty(true);
  };

  const handleSave = () => {
    const canvas = canvasRef.current;
    if (!canvas || isEmpty) return;
    setSaving(true);
    canvas.toBlob((blob) => {
      setSaving(false);
      if (!blob) return;
      onSave(new File([blob], "signature.png", { type: "image/png" }));
    }, "image/png");
  };

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      fullWidth
      maxWidth="sm"
      dir="rtl"
      onClose={onClose}
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
    >
      <Box
        sx={{
          background: `linear-gradient(135deg, ${BLUE} 0%, #13a2d4 100%)`,
          px: 2.5,
          py: 1.75,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "12px",
              display: "grid",
              placeItems: "center",
              bgcolor: "rgba(255,255,255,0.18)",
              color: "#fff",
              fontSize: 22,
            }}
          >
            <HiOutlinePencil />
          </Box>
          <Box>
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 18 }}>
              التوقيع
            </Typography>
            <Typography
              sx={{ color: "rgba(255,255,255,0.82)", fontSize: 12.5 }}
            >
              وقّع داخل المربع بالماوس او بالاصبع
            </Typography>
          </Box>
        </Stack>
        <IconButton
          onClick={onClose}
          sx={{
            color: "#fff",
            "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
          }}
        >
          <IoClose />
        </IconButton>
      </Box>

      <DialogContent sx={{ bgcolor: "#f4f6f8", p: 2.5 }}>
        <Box
          sx={{
            position: "relative",
            border: "1px dashed #cbd5e1",
            borderRadius: 2,
            bgcolor: "#fff",
            overflow: "hidden",
          }}
        >
          <Box
            component="canvas"
            ref={canvasRef}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerLeave={handlePointerUp}
            onPointerCancel={handlePointerUp}
            sx={{
              display: "block",
              width: "100%",
              height: { xs: 220, sm: 260 },
              touchAction: "none",
              cursor: "crosshair",
            }}
          />
          {isEmpty && (
            <Typography
              sx={{
                position: "absolute",
                inset: 0,
                display: "grid",
                placeItems: "center",
                color: "#98a2b3",
                fontSize: 14,
                pointerEvents: "none",
              }}
            >
              وقّع هنا
            </Typography>
          )}
        </Box>
      </DialogContent>

      <Box
        sx={{
          px: 2.5,
          py: 2,
          bgcolor: "#fff",
          borderTop: "1px solid #edf0f3",
          display: "flex",
          justifyContent: "space-between",
          gap: 1.5,
        }}
      >
        <Button
          onClick={handleClear}
          startIcon={<HiOutlineTrash />}
          disabled={isEmpty}
          sx={{ color: "#b42318" }}
        >
          مسح
        </Button>
        <Stack direction="row" spacing={1.5}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              color: "#475467",
              borderColor: "#d0d5dd",
              px: 3,
              "&:hover": { borderColor: "#98a2b3", bgcolor: "#f9fafb" },
            }}
          >
            إلغاء
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={isEmpty || saving}
            sx={{
              bgcolor: BLUE,
              px: 4,
              boxShadow: "0 4px 12px rgba(12,134,182,0.3)",
              "&:hover": { bgcolor: BLUE_DARK },
            }}
          >
            حفظ التوقيع
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );
};

export default SignatureDialog;
